import { Trans, useLingui } from '@lingui/react/macro'
import { Checkbox } from 'designSystem/components/Checkbox'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import {
  CleaningOp,
  CleaningPlan,
  RemovedPointsSeries,
  TagStats,
} from 'features/timeSeries/experiment/types'
import { FC, useMemo } from 'react'
import styled from 'styled-components'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.75rem 0.85rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.forecast.background2};
`

const Header = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: baseline;
  justify-content: space-between;
`

const Title = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  word-break: break-word;
`

const Badge = styled.span<{ $warn?: boolean }>`
  font-size: 0.7rem;
  font-variant-numeric: tabular-nums;
  padding: 0.15rem 0.45rem;
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $warn }) => ($warn ? '#ef4444' : theme.forecast.border)};
  color: ${({ theme, $warn }) =>
    $warn ? '#ef4444' : theme.forecast.secondaryText};
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

const Controls = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(11rem, 1fr));
  gap: 0.55rem 0.75rem;
  font-size: 0.72rem;
`

const Ctrl = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Dual = styled.div`
  display: flex;
  gap: 0.35rem;
  align-items: center;
`

const Input = styled.input`
  padding: 0.25rem 0.4rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  min-width: 0;
  width: 100%;
  ${mobileInputTextStyles}
`

const Select = styled.select`
  padding: 0.25rem 0.4rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.75rem;
  ${mobileInputTextStyles}
`

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.72rem;
  cursor: pointer;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.7rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

const findOp = (ops: CleaningOp[], kind: string) =>
  ops.find((o) => o.op === kind)

const findDenoise = (ops: CleaningOp[]) =>
  ops.find((o) => o.op === 'denoise_median' || o.op === 'denoise_mean')

const upsertOp = (ops: CleaningOp[], op: CleaningOp): CleaningOp[] => {
  const kinds =
    op.op === 'denoise_median' || op.op === 'denoise_mean'
      ? ['denoise_median', 'denoise_mean']
      : [op.op]
  const next = ops.filter((o) => !kinds.includes(o.op))
  next.push(op)
  return next
}

const removeOps = (ops: CleaningOp[], ...kinds: string[]): CleaningOp[] =>
  ops.filter((o) => !kinds.includes(o.op))

export interface CleaningControlsProps {
  tag: string
  isTarget?: boolean
  removed?: RemovedPointsSeries | null
  /**
   * Partial because only the bounds and counts are read, and callers outside
   * the lab (the sentinel-design context bundle) report a subset.
   */
  tagStats?: Partial<TagStats> | null
  /** Raw (pre-clean) stats — preferred for range slider bounds. */
  rawStats?: Partial<TagStats> | null
  cleaningPlan: CleaningPlan
  onCleaningPlanChange: (plan: CleaningPlan) => void
}

/**
 * Per-tag cleaning knobs (range, quantile clip, frozen, denoise, gate) from
 * the old TagChartCard, with its Plotly time-series chart removed: the
 * cleaned series and removed-point markers now render on the main viewer
 * chart, which has zoom/tooltip/ruler.
 */
export const CleaningControls: FC<CleaningControlsProps> = ({
  tag,
  isTarget,
  removed,
  tagStats,
  rawStats,
  cleaningPlan,
  onCleaningPlanChange,
}) => {
  const { t } = useLingui()

  const tagClean = cleaningPlan.tags?.[tag] || { ops: [] as CleaningOp[] }
  const ops = tagClean.ops || []

  const rangeOp = findOp(ops, 'range')
  const quantileOp = findOp(ops, 'clip_quantile')
  const frozenOp = findOp(ops, 'frozen')
  const gateOp = findOp(ops, 'gate')
  const denoiseOp = findDenoise(ops)

  const bounds = useMemo(() => {
    const lo = rawStats?.min ?? tagStats?.min
    const hi = rawStats?.max ?? tagStats?.max
    if (
      lo === null ||
      lo === undefined ||
      hi === null ||
      hi === undefined ||
      !Number.isFinite(lo) ||
      !Number.isFinite(hi) ||
      lo === hi
    ) {
      return { min: 0, max: 100 }
    }
    const pad = (hi - lo) * 0.02 || 1
    return { min: lo - pad, max: hi + pad }
  }, [rawStats, tagStats])

  const patchOps = (nextOps: CleaningOp[]) => {
    onCleaningPlanChange({
      ...cleaningPlan,
      tags: {
        ...cleaningPlan.tags,
        [tag]: {
          ...tagClean,
          ops: nextOps,
        },
      },
    })
  }

  const removedCount = removed?.removed_count ?? removed?.ts?.length ?? 0
  const removedPct = removed?.removed_pct
  const kept =
    tagStats?.cleaned_observed ??
    (tagStats?.observed !== null &&
    tagStats?.observed !== undefined &&
    removedCount
      ? Math.max(0, tagStats.observed - removedCount)
      : tagStats?.observed)
  const formattedRemovedCount = removedCount.toLocaleString()
  const removedPercentageSuffix =
    removedPct !== null && removedPct !== undefined
      ? ` · ${(removedPct * 100).toFixed(1)}%`
      : ''
  const formattedKept = kept?.toLocaleString() ?? ''

  return (
    <Card>
      <Header>
        <Title>
          <TagLabel tagId={tag}>
            <span>
              {short(tag)}
              {isTarget ? t` · target` : ''}
            </span>
          </TagLabel>
        </Title>
        <Badge $warn={removedCount > 0}>
          {removedCount > 0
            ? t`removed ${formattedRemovedCount} pts${removedPercentageSuffix}`
            : t`no removals`}
          {kept !== null && kept !== undefined
            ? t` · kept ${formattedKept}`
            : ''}
        </Badge>
      </Header>

      <Controls>
        <Ctrl>
          <KnobHelp
            label={t`Range min / max`}
            help={t`Drop points outside this physical range (sets them missing). Bounds default from raw tag stats.`}
          />
          <CheckRow>
            <Checkbox
              checked={!!rangeOp}
              onCheckedChange={(v) => {
                if (v) {
                  patchOps(
                    upsertOp(ops, {
                      op: 'range',
                      min: rangeOp?.min ?? bounds.min,
                      max: rangeOp?.max ?? bounds.max,
                    })
                  )
                } else {
                  patchOps(removeOps(ops, 'range'))
                }
              }}
            />
            <Trans>Enable range</Trans>
          </CheckRow>
          <Dual>
            <Input
              type='number'
              step='any'
              disabled={!rangeOp}
              value={rangeOp?.min ?? bounds.min}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'range',
                    min: Number(e.target.value),
                    max: rangeOp?.max ?? bounds.max,
                  })
                )
              }
            />
            <Input
              type='number'
              step='any'
              disabled={!rangeOp}
              value={rangeOp?.max ?? bounds.max}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'range',
                    min: rangeOp?.min ?? bounds.min,
                    max: Number(e.target.value),
                  })
                )
              }
            />
          </Dual>
          <Dual>
            <Input
              type='range'
              min={bounds.min}
              max={bounds.max}
              step={(bounds.max - bounds.min) / 200 || 1}
              disabled={!rangeOp}
              value={rangeOp?.min ?? bounds.min}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'range',
                    min: Number(e.target.value),
                    max: rangeOp?.max ?? bounds.max,
                  })
                )
              }
            />
            <Input
              type='range'
              min={bounds.min}
              max={bounds.max}
              step={(bounds.max - bounds.min) / 200 || 1}
              disabled={!rangeOp}
              value={rangeOp?.max ?? bounds.max}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'range',
                    min: rangeOp?.min ?? bounds.min,
                    max: Number(e.target.value),
                  })
                )
              }
            />
          </Dual>
        </Ctrl>

        <Ctrl>
          <KnobHelp
            label={t`Quantile clip`}
            help={t`Drop points outside these data quantiles (0–1). Typical: 0.01 / 0.99.`}
          />
          <CheckRow>
            <Checkbox
              checked={!!quantileOp}
              onCheckedChange={(v) => {
                if (v) {
                  patchOps(
                    upsertOp(ops, {
                      op: 'clip_quantile',
                      low: quantileOp?.low ?? 0.01,
                      high: quantileOp?.high ?? 0.99,
                    })
                  )
                } else {
                  patchOps(removeOps(ops, 'clip_quantile'))
                }
              }}
            />
            <Trans>Enable quantile</Trans>
          </CheckRow>
          <Dual>
            <Input
              type='number'
              min={0}
              max={1}
              step={0.01}
              disabled={!quantileOp}
              value={quantileOp?.low ?? 0.01}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'clip_quantile',
                    low: Number(e.target.value),
                    high: quantileOp?.high ?? 0.99,
                  })
                )
              }
            />
            <Input
              type='number'
              min={0}
              max={1}
              step={0.01}
              disabled={!quantileOp}
              value={quantileOp?.high ?? 0.99}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'clip_quantile',
                    low: quantileOp?.low ?? 0.01,
                    high: Number(e.target.value),
                  })
                )
              }
            />
          </Dual>
          <Dual>
            <Input
              type='range'
              min={0}
              max={0.5}
              step={0.01}
              disabled={!quantileOp}
              value={quantileOp?.low ?? 0.01}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'clip_quantile',
                    low: Number(e.target.value),
                    high: quantileOp?.high ?? 0.99,
                  })
                )
              }
            />
            <Input
              type='range'
              min={0.5}
              max={1}
              step={0.01}
              disabled={!quantileOp}
              value={quantileOp?.high ?? 0.99}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'clip_quantile',
                    low: quantileOp?.low ?? 0.01,
                    high: Number(e.target.value),
                  })
                )
              }
            />
          </Dual>
        </Ctrl>

        <Ctrl>
          <KnobHelp
            label={t`Frozen sensor`}
            help={t`Drop runs of more than max_repeat identical consecutive values (stuck sensor).`}
          />
          <CheckRow>
            <Checkbox
              checked={!!frozenOp}
              onCheckedChange={(v) => {
                if (v) {
                  patchOps(
                    upsertOp(ops, {
                      op: 'frozen',
                      max_repeat: frozenOp?.max_repeat ?? 6,
                    })
                  )
                } else {
                  patchOps(removeOps(ops, 'frozen'))
                }
              }}
            />
            <Trans>Enable frozen</Trans>
          </CheckRow>
          <Input
            type='number'
            min={1}
            disabled={!frozenOp}
            value={frozenOp?.max_repeat ?? 6}
            onChange={(e) =>
              patchOps(
                upsertOp(ops, {
                  op: 'frozen',
                  max_repeat: Number(e.target.value) || 6,
                })
              )
            }
          />
        </Ctrl>

        <Ctrl>
          <KnobHelp
            label={t`Denoise window`}
            help={t`Rolling median/mean over surviving points (experiment-only; does not remove points).`}
          />
          <CheckRow>
            <Checkbox
              checked={!!denoiseOp}
              onCheckedChange={(v) => {
                if (v) {
                  patchOps(
                    upsertOp(ops, {
                      op:
                        denoiseOp?.op === 'denoise_mean'
                          ? 'denoise_mean'
                          : 'denoise_median',
                      window: denoiseOp?.window ?? 3,
                    })
                  )
                } else {
                  patchOps(removeOps(ops, 'denoise_median', 'denoise_mean'))
                }
              }}
            />
            <Trans>Enable denoise</Trans>
          </CheckRow>
          <Dual>
            <Select
              disabled={!denoiseOp}
              value={denoiseOp?.op ?? 'denoise_median'}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: e.target.value,
                    window: denoiseOp?.window ?? 3,
                  })
                )
              }>
              <option value='denoise_median'>median</option>
              <option value='denoise_mean'>mean</option>
            </Select>
            <Input
              type='number'
              min={1}
              disabled={!denoiseOp}
              value={denoiseOp?.window ?? 3}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: denoiseOp?.op ?? 'denoise_median',
                    window: Number(e.target.value) || 3,
                  })
                )
              }
            />
          </Dual>
          <Input
            type='range'
            min={1}
            max={31}
            step={2}
            disabled={!denoiseOp}
            value={denoiseOp?.window ?? 3}
            onChange={(e) =>
              patchOps(
                upsertOp(ops, {
                  op: denoiseOp?.op ?? 'denoise_median',
                  window: Number(e.target.value) || 3,
                })
              )
            }
          />
        </Ctrl>

        <Ctrl>
          <KnobHelp
            label={t`Gate min / max`}
            help={t`Keep only rows where this tag is in [min, max]. Rows where it falls outside the range are removed across all tags (operating-regime filter).`}
          />
          <CheckRow>
            <Checkbox
              checked={!!gateOp}
              onCheckedChange={(v) => {
                if (v) {
                  patchOps(
                    upsertOp(ops, {
                      op: 'gate',
                      min: gateOp?.min ?? bounds.min,
                      max: gateOp?.max ?? bounds.max,
                    })
                  )
                } else {
                  patchOps(removeOps(ops, 'gate'))
                }
              }}
            />
            <Trans>Enable gate</Trans>
          </CheckRow>
          <Dual>
            <Input
              type='number'
              step='any'
              disabled={!gateOp}
              value={gateOp?.min ?? bounds.min}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'gate',
                    min: Number(e.target.value),
                    max: gateOp?.max ?? bounds.max,
                  })
                )
              }
            />
            <Input
              type='number'
              step='any'
              disabled={!gateOp}
              value={gateOp?.max ?? bounds.max}
              onChange={(e) =>
                patchOps(
                  upsertOp(ops, {
                    op: 'gate',
                    min: gateOp?.min ?? bounds.min,
                    max: Number(e.target.value),
                  })
                )
              }
            />
          </Dual>
          <Hint>
            <Trans>Gate drops rows for every tag, not just this one.</Trans>
          </Hint>
        </Ctrl>
      </Controls>
    </Card>
  )
}
